<template>
  <a-drawer
    :title="`${dramaDetail?.name || ''}（${detail?.type2target_text || ''}）`"
    width="800"
    :visible="visible"
    :closable="false"
    :footer-style="{ textAlign: 'right' }"
    @close="onClose"
  >
    <!-- 提示信息 -->
    <a-alert
      message="注意：任务创建3个小时内系统会自动同步任务状态，超过3个小时未处理完成的任务需手动同步任务状态。"
      type="warning"
      show-icon
      style="margin-bottom: 16px;"
    />
    <!-- 工具栏 -->
    <div class="toolbar" style="margin-bottom: 16px; text-align: right;">
      <a-button style="margin-right: 8px;" :loading="isExportEpLoading" :disabled="isExportEpLoading" @click="touchExportEpSubtitle">导出分集字幕</a-button>
      <a-button style="margin-right: 8px;" :loading="isExportAllLoading" :disabled="isExportAllLoading" @click="touchExportAllSubtitle">导出全集字幕</a-button>
      <a-button 
        type="primary" 
        :loading="isLoading"
        @click="handleBatchSync"
      >
        批量同步状态
      </a-button>
    </div>
    <!-- 列表 -->
    <a-table
      class="table-view"
      :data-source="dataSource"
      :columns="columns"
      :row-key="record => record.id"
      :pagination="false"
      :loading="isLoading"
      @change="handleTableChange"
    >
      <!-- 自定义行内容 -->
      <template #bodyCell="{ column, record }">
        <!-- 任务状态 -->
        <template v-if="column.key === 'status'"> 
          <!-- 待处理 -->
          <template v-if="record.status === VideoEqStatus.pending.value">
            <a-badge :color="VideoEqStatus.pending.color" :text="VideoEqStatus.pending.text"></a-badge>
          </template>
          <!-- 处理中 -->
          <template v-else-if="record.status === VideoEqStatus.processing.value">
            <a-badge :color="VideoEqStatus.processing.color" :text="VideoEqStatus.processing.text"></a-badge>
          </template>
          <!-- 已暂停 -->
          <template v-else-if="record.status === VideoEqStatus.suspended.value">
            <a-badge :color="VideoEqStatus.suspended.color" :text="VideoEqStatus.suspended.text"></a-badge>
          </template>
          <!-- 已完成 -->
          <template v-else-if="record.status === VideoEqStatus.completed.value">
            <a-badge :color="VideoEqStatus.completed.color" :text="VideoEqStatus.completed.text"></a-badge>
          </template>
          <!-- 失败 -->
          <template v-else-if="record.status === VideoEqStatus.failed.value">
            <a-badge :color="VideoEqStatus.failed.color" :text="VideoEqStatus.failed.text"></a-badge>
          </template>
          <!-- 未知状态 -->
          <template v-else>
            <a-badge color="#999999" text="未知状态"></a-badge>
          </template>
        </template>
        <!-- 操作 -->
        <template v-else-if="column.key === 'operation'">
          <!-- 编辑/预览 -->
          <a
            v-if="record.status === VideoEqStatus.completed.value || record.status === VideoEqStatus.failed.value || record.status === VideoEqStatus.suspended.value"
            class="operation-item" 
            @click="touchEdit(record)"
          >编辑/预览</a>
          <a class="operation-item" style="color: rgba(0, 0, 0, 0.4);" v-else>编辑/预览</a>
          <!-- 重新翻译 -->
          <a-popconfirm
            v-if="record.status === VideoEqStatus.completed.value || record.status === VideoEqStatus.failed.value || record.status === VideoEqStatus.suspended.value"
            title="确认需要重新翻译吗？"
            @confirm="touchTranslate(record)"
          >
            <a class="operation-item">重新翻译</a>
          </a-popconfirm>
          <a class="operation-item" style="color: rgba(0, 0, 0, 0.4);" v-else>重新翻译</a>
          <!-- 同步状态 -->
          <a class="operation-item" @click="touchSync(record)">同步状态</a>
        </template>
        <template v-else>
          {{ record[column.key] || '-' }}
        </template>
      </template>
    </a-table>
    <!-- 视频编辑弹窗 -->
    <VideoEdit ref="RefVideoEdit" @success="getList"></VideoEdit>
  </a-drawer>
</template>

<script setup>
// @ts-nocheck
import { onBeforeMount, reactive, ref } from 'vue'
import { message, Modal } from 'ant-design-vue'
// 引入 dayjs 及 扩展函数
import dayjs from 'dayjs'
// 引入公共方法
import Pub from '@/utils/public'
// 引入常量列表
import { VideoEqStatus } from '@/utils/constantList'
// 引入 API
import { taskEqList, taskEqTask, taskSysJob, taskSysTask, downloadSubtitle } from '@/api/admin'
// 引入下载工具
import { DOWLOAD_FILE_URL_PRO } from '@/utils/download'
// 引入视频编辑弹窗组件
import VideoEdit from './VideoEdit.vue'

// emit
const emit = defineEmits(['success'])
// 视频编辑弹窗引用
let RefVideoEdit = ref(null)
// 显示
let visible = ref(false)
// 加载
let isLoading = ref(false)
// 导出分集字幕加载
let isExportEpLoading = ref(false)
// 导出全集字幕加载
let isExportAllLoading = ref(false)
// 剧集详情
let dramaDetail = ref({})
// 修改对象
let detail = ref({})
// 数据源
let dataSource = ref([])
// 分页信息
let pagination = reactive({
  total: 0,
  current: 1,
  pageSize: 1000,
  showSizeChanger: true
})
// 列数据源 - 根据图片中的字段标题
let columns = ref([
  {
    title: '集数',
    dataIndex: 'eq_number',
    key: 'eq_number'
  },
  {
    title: '火山任务ID',
    dataIndex: 'project_id',
    key: 'project_id'
  },
  {
    title: '任务状态',
    dataIndex: 'status',
    key: 'status'
  },
  {
    title: '操作',
    dataIndex: 'operation',
    key: 'operation'
  }
])

// 显示弹窗
function showDrawer(drama, record) {
  visible.value = true
  dramaDetail.value = drama || {}
  detail.value = record || {}
  // 初始化获取列表
  getList()
}

// 分页处理
function handleTableChange(p, filters, sorter) {
  pagination.current = p.current
  pagination.pageSize = p.pageSize
  // 获取列表
  getList()
}

// 延迟函数，用于串行下载时给浏览器时间处理每个下载
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// 导出分集字幕（串行下载，避免浏览器同时下载数量限制导致部分文件未真正触发）
async function touchExportEpSubtitle() {
  const list = dataSource.value.filter(item => item.audio_url)
  if (!list.length) {
    message.warning('暂无可下载的分集字幕')
    return
  }
  isExportEpLoading.value = true
  const failed = []
  try {
    for (let i = 0; i < list.length; i++) {
      const item = list[i]
      const ext = item.audio_url.split('.').pop().split('?')[0]
      const filename = `${dramaDetail.value.name || ''}_${item.eq_number}.${ext}`
      try {
        await DOWLOAD_FILE_URL_PRO(item.audio_url, filename)
        // 每个文件下载后间隔 400ms，确保浏览器真正处理并保存，避免被合并/忽略
        if (i < list.length - 1) await delay(400)
      } catch (err) {
        failed.push({ item, reason: err })
      }
    }
    if (failed.length === 0) {
      message.success(`导出完成，共 ${list.length} 个文件`)
    } else if (failed.length === list.length) {
      const errMsg = failed[0].reason?.message || String(failed[0].reason)
      message.error(`全部导出失败（${failed.length} 个）。错误：${errMsg}`)
    } else {
      const failedNames = failed.map(f => `第${f.item.eq_number}集`).join('、')
      const errMsg = failed[0].reason?.message || String(failed[0].reason)
      message.error(`部分导出失败：${failedNames}（共 ${failed.length} 个）。错误：${errMsg}`)
    }
  } catch (err) {
    message.error('导出异常：' + (err?.message || err))
  } finally {
    isExportEpLoading.value = false
  }
}

// 导出全集字幕
async function touchExportAllSubtitle() {
  isExportAllLoading.value = true
  try {
    const res = await downloadSubtitle({ task_id: detail.value.id })
    const { code, data } = res
    if (code === 0) {
      const subtitleUrl = data?.result?.long_subtitle_url
      if (!subtitleUrl) {
        message.warning('暂无全集字幕可下载')
        return
      }
      const ext = subtitleUrl.split('.').pop().split('?')[0]
      const filename = `${dramaDetail.value.name || detail.value.id}.${ext}`
      await DOWLOAD_FILE_URL_PRO(subtitleUrl, filename)
      message.success('导出完成')
    } else {
      message.error(res.message || '导出失败')
    }
  } catch {
    message.error('导出失败')
  } finally {
    isExportAllLoading.value = false
  }
}

// 编辑/预览
function touchEdit(record) {
  console.log('预览', record)
  RefVideoEdit.value.showEdit(dramaDetail.value, record)
}

// 同步状态（单个）
function touchSync(record) {
  isLoading.value = true
  const params = {
    task_id: detail.value.id,
    space_name: record.space_name,
    project_id: record.project_id,
    db_id: record.db_id,
    type: 1
  }
  taskSysJob(params).then(res => {
    isLoading.value = false
    const { code, msg, data } = res
    if (code === 0) {
      message.success('同步成功')
      // 更新列表
      getList()
    } else {
      message.error(res.message || '同步失败')
    }
  }).catch(() => {
    isLoading.value = false
    message.error('同步失败')
  })
}

// 批量同步状态
function handleBatchSync() {
  isLoading.value = true
  // 一次性调用接口
  const params = {
    task_id: detail.value.id
  }
  
  taskSysTask(params).then(res => {
    isLoading.value = false
    const { code, data } = res
    if (code === 0) {
      message.success(data.message || `批量同步成功`)
      // 更新列表
      getList()
    } else {
      message.error(res.message || '批量同步失败')
    }
  }).catch(() => {
    isLoading.value = false
    message.error('批量同步失败')
  })
}

// 开始翻译
function touchTranslate(record) {
  // 处理中
  isLoading.value = true
  taskEqTask({ task_id: detail.value.id, eq_number: record.eq_number }).then(res => {
    isLoading.value = false
    const { code, msg, data } = res
    if (code === 0) {
      message.success(res.message)
      // 更新列表
      getList()
    } else {
      message.error(res.message)
    }
  }).catch(() => {
    isLoading.value = false
    message.error('重新翻译失败')
  })
}

// 获取列表
function getList() {
  isLoading.value = true
  const params = {
    task_id: detail.value.id,
    page: pagination.current,
    page_size: pagination.pageSize
  }
  // 暂时使用 YoutubeFilmlibrary 的接口
  taskEqList(params).then(res => {
    isLoading.value = false
    const { code, data } = res
    if (code === 0) {
      dataSource.value = data
    }
  }).catch(() => {
    isLoading.value = false
  })
}

// 关闭弹窗
function onClose() {
  if (!isLoading.value) {
    visible.value = false
    dramaDetail.value = {}
    detail.value = null
    // 重置分页
    pagination.current = 1
    pagination.total = 0
    dataSource.value = []
  }
}

// 暴露方法给父组件
defineExpose({
  showDrawer
})
</script>

<style scoped>
.tool-row {
  margin-bottom: 16px;
}

.tool-title {
  text-align: right;
}

.tool-item {
  width: 100%;
}

.table-view {
  background: #fff;
  border-radius: 6px;
}

.operation-item {
  color: #1890ff;
  margin-right: 16px;
  cursor: pointer;
}

.operation-item:hover {
  color: #40a9ff;
}
</style>
